import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calendar, DollarSign, ToggleLeft, ToggleRight, History } from 'lucide-react';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import Loading from '../components/common/Loading';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import Input from '../components/common/Input';
import { AssociatePlan, Plan, AttachPlanData, AssociatePlanLog } from '../types';
import api from '../services/api';
import { planService } from '../services/planService';
import { formatCurrency, formatDate } from '../utils/formatters';

const AssociatePlans: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [associateName, setAssociateName] = useState('');
  const [plans, setPlans] = useState<AssociatePlan[]>([]);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<AssociatePlan | null>(null);
  const [processing, setProcessing] = useState(false);
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [statusChangeNotes, setStatusChangeNotes] = useState('');
  const [planLogs, setPlanLogs] = useState<AssociatePlanLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [formData, setFormData] = useState<AttachPlanData>({
    plan_id: 0,
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: '',
    status: 'ativo',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadAssociatePlans();
    loadAvailablePlans();
    loadAssociateInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAssociateInfo = async () => {
    try {
      const response = await api.get(`/associates/${id}`);
      setAssociateName(response.data.nome || response.data.name);
    } catch (err) {
      console.error('Erro ao carregar informações do associado');
    }
  };

  const loadAssociatePlans = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/associates/${id}/plans`);
      setPlans(response.data);
    } catch (err: any) {
      setError('Erro ao carregar planos do associado');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePlans = async () => {
    try {
      const response = await api.get('/plans', {
        params: { status: 'ativo' }
      });
      setAvailablePlans(response.data);
    } catch (err: any) {
      console.error('Erro ao carregar planos disponíveis');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.plan_id || formData.plan_id === 0) {
      errors.plan_id = 'Selecione um plano';
    }

    if (!formData.data_inicio) {
      errors.data_inicio = 'Data de início é obrigatória';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddPlan = async () => {
    if (!validate()) {
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/associates/${id}/plans`, formData);
      setSuccess('Plano vinculado com sucesso');
      await loadAssociatePlans();
      setShowAddModal(false);
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao vincular plano');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemovePlan = async () => {
    if (!selectedPlan) return;

    setProcessing(true);
    try {
      await api.delete(`/associates/${id}/plans/${selectedPlan.id}`);
      setSuccess('Plano removido com sucesso');
      await loadAssociatePlans();
      setShowRemoveModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao remover plano');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedPlan) return;

    // Validar motivo
    if (!statusChangeReason.trim() || statusChangeReason.trim().length < 5) {
      setError('O motivo é obrigatório e deve ter pelo menos 5 caracteres');
      return;
    }

    const newStatus = selectedPlan.pivot.status === 'ativo' ? 'cancelado' : 'ativo';
    const actionText = newStatus === 'ativo' ? 'reativado' : 'inativado';

    setProcessing(true);
    try {
      await api.patch(`/associates/${id}/plans/${selectedPlan.id}/status`, {
        status: newStatus,
        reason: statusChangeReason.trim(),
        notes: statusChangeNotes.trim() || null
      });
      setSuccess(`Plano ${actionText} com sucesso`);
      await loadAssociatePlans();
      setShowStatusModal(false);
      setStatusChangeReason('');
      setStatusChangeNotes('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.errors?.reason?.[0] || 'Erro ao alterar status do plano');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      plan_id: 0,
      data_inicio: new Date().toISOString().split('T')[0],
      data_fim: '',
      status: 'ativo',
    });
    setFormErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'success';
      case 'cancelado':
        return 'danger';
      case 'expirado':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'Ativo';
      case 'cancelado':
        return 'Cancelado';
      case 'expirado':
        return 'Expirado';
      default:
        return status;
    }
  };

  const loadPlanLogs = async (plan: AssociatePlan) => {
    if (!id) return;

    try {
      setLoadingLogs(true);
      setSelectedPlan(plan);
      const logs = await planService.getPlanLogs(parseInt(id), plan.id);
      setPlanLogs(logs);
      setShowLogsModal(true);
    } catch (err) {
      setError('Erro ao carregar histórico de logs. Tente novamente.');
    } finally {
      setLoadingLogs(false);
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'status_changed':
        return 'Alteração de Status';
      case 'created':
        return 'Plano Criado';
      case 'removed':
        return 'Plano Removido';
      default:
        return action;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Button
          variant="secondary"
          onClick={() => navigate('/associados')}
          className="mb-4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Voltar
        </Button>

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Planos do Associado</h1>
            <p className="text-gray-600">{associateName}</p>
          </div>
          <Button onClick={openAddModal}>
            <Plus className="w-5 h-5 mr-2" />
            Adicionar Plano
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError('')} className="mb-6">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} className="mb-6">
          {success}
        </Alert>
      )}

      {plans.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Nenhum plano vinculado
          </h3>
          <p className="text-gray-600 mb-4">
            Este associado ainda não possui nenhum plano
          </p>
          <Button onClick={openAddModal}>
            <Plus className="w-5 h-5 mr-2" />
            Adicionar Primeiro Plano
          </Button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {plan.nome}
                  </h3>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(plan.valor)}
                    <span className="text-sm text-gray-500 font-normal">/mês</span>
                  </p>
                </div>
                <Badge variant={getStatusBadgeVariant(plan.pivot.status)}>
                  {getStatusLabel(plan.pivot.status)}
                </Badge>
              </div>

              {plan.descricao && (
                <p className="text-gray-600 text-sm mb-4">{plan.descricao}</p>
              )}

              <div className="space-y-2 mb-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    Início: {formatDate(plan.pivot.data_inicio)}
                  </span>
                </div>
                {plan.pivot.data_fim && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      Fim: {formatDate(plan.pivot.data_fim)}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={plan.pivot.status === 'ativo' ? 'secondary' : 'primary'}
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowStatusModal(true);
                  }}
                  className="flex-1"
                  title={plan.pivot.status === 'ativo' ? 'Inativar plano' : 'Reativar plano'}
                >
                  {plan.pivot.status === 'ativo' ? (
                    <ToggleLeft className="w-4 h-4 mr-1" />
                  ) : (
                    <ToggleRight className="w-4 h-4 mr-1" />
                  )}
                  {plan.pivot.status === 'ativo' ? 'Inativar' : 'Reativar'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadPlanLogs(plan)}
                  title="Ver histórico de alterações"
                  disabled={loadingLogs}
                >
                  <History className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowRemoveModal(true);
                  }}
                  title="Remover plano"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal para Adicionar Plano */}
      {showAddModal && (
        <Modal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          title="Adicionar Plano"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plano <span className="text-red-500">*</span>
              </label>
              <select
                name="plan_id"
                value={formData.plan_id}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${
                  formErrors.plan_id ? 'border-red-500' : 'border-gray-300'
                } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              >
                <option value={0}>Selecione um plano</option>
                {availablePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.nome} - {formatCurrency(plan.valor)}/mês
                  </option>
                ))}
              </select>
              {formErrors.plan_id && (
                <p className="mt-1 text-sm text-red-600">{formErrors.plan_id}</p>
              )}
            </div>

            <Input
              label="Data de Início"
              type="date"
              name="data_inicio"
              value={formData.data_inicio}
              onChange={handleChange}
              error={formErrors.data_inicio}
              required
            />

            <Input
              label="Data de Fim"
              type="date"
              name="data_fim"
              value={formData.data_fim || ''}
              onChange={handleChange}
              error={formErrors.data_fim}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ativo">Ativo</option>
                <option value="cancelado">Cancelado</option>
                <option value="expirado">Expirado</option>
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowAddModal(false)}
                className="flex-1"
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddPlan}
                loading={processing}
                disabled={processing}
                className="flex-1"
              >
                Adicionar Plano
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal para Alterar Status do Plano */}
      {showStatusModal && selectedPlan && (
        <Modal
          isOpen={showStatusModal}
          onClose={() => {
            setShowStatusModal(false);
            setStatusChangeReason('');
            setStatusChangeNotes('');
          }}
          title={selectedPlan.pivot.status === 'ativo' ? 'Inativar Plano' : 'Reativar Plano'}
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Você está prestes a{' '}
              <strong>{selectedPlan.pivot.status === 'ativo' ? 'inativar' : 'reativar'}</strong> o plano{' '}
              <strong>{selectedPlan.nome}</strong>.
            </p>
            {selectedPlan.pivot.status === 'ativo' ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Atenção:</strong> Ao inativar o plano, o associado não terá mais acesso aos benefícios,
                  mas o histórico de pagamentos será mantido.
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Observação:</strong> Ao reativar o plano, o associado voltará a ter acesso aos benefícios.
                </p>
              </div>
            )}

            {/* Campo obrigatório: Motivo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo <span className="text-red-500">*</span>
              </label>
              <textarea
                value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value)}
                placeholder="Informe o motivo da alteração (mínimo 5 caracteres)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                maxLength={500}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {statusChangeReason.length}/500 caracteres
              </p>
            </div>

            {/* Campo opcional: Observações */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações Adicionais (opcional)
              </label>
              <textarea
                value={statusChangeNotes}
                onChange={(e) => setStatusChangeNotes(e.target.value)}
                placeholder="Informações complementares..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={2}
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 mt-1">
                {statusChangeNotes.length}/1000 caracteres
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowStatusModal(false)}
                className="flex-1"
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button
                variant={selectedPlan.pivot.status === 'ativo' ? 'danger' : 'primary'}
                onClick={handleToggleStatus}
                loading={processing}
                disabled={processing}
                className="flex-1"
              >
                Confirmar {selectedPlan.pivot.status === 'ativo' ? 'Inativação' : 'Reativação'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal para Remover Plano */}
      {showRemoveModal && selectedPlan && (
        <Modal
          isOpen={showRemoveModal}
          onClose={() => setShowRemoveModal(false)}
          title="Remover Plano"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja remover o plano{' '}
              <strong>{selectedPlan.nome}</strong> deste associado?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Atenção:</strong> Esta ação remove permanentemente o plano e todo o histórico relacionado.
                Se deseja apenas desativar temporariamente, use o botão "Inativar" ao invés de remover.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowRemoveModal(false)}
                className="flex-1"
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleRemovePlan}
                loading={processing}
                disabled={processing}
                className="flex-1"
              >
                Confirmar Remoção
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Histórico de Logs */}
      {showLogsModal && selectedPlan && (
        <Modal
          isOpen={showLogsModal}
          onClose={() => {
            setShowLogsModal(false);
            setPlanLogs([]);
          }}
          title={`Histórico de Alterações - ${selectedPlan.nome}`}
        >
          <div className="space-y-4">
            {loadingLogs ? (
              <div className="flex justify-center py-8">
                <Loading />
              </div>
            ) : planLogs.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">Nenhum registro de alteração encontrado.</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {planLogs.map((log, index) => (
                  <div
                    key={log.id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 relative"
                  >
                    {/* Timeline line */}
                    {index < planLogs.length - 1 && (
                      <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-gray-300" />
                    )}

                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 z-10">
                        <History className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {getActionLabel(log.action)}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {new Date(log.created_at).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                          {log.action === 'status_changed' && log.previous_status && log.new_status && (
                            <div className="flex items-center gap-2">
                              <Badge variant={getStatusBadgeVariant(log.previous_status)}>
                                {getStatusLabel(log.previous_status)}
                              </Badge>
                              <span className="text-gray-400">→</span>
                              <Badge variant={getStatusBadgeVariant(log.new_status)}>
                                {getStatusLabel(log.new_status)}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* User info */}
                    {(log.user || log.metadata?.user_name) && (
                      <div className="ml-11 mb-2">
                        <p className="text-sm text-gray-600">
                          <strong>Usuário:</strong>{' '}
                          {log.user?.name || log.metadata?.user_name}
                          {(log.user?.email || log.metadata?.user_email) && (
                            <span className="text-gray-500">
                              {' '}({log.user?.email || log.metadata?.user_email})
                            </span>
                          )}
                        </p>
                      </div>
                    )}

                    {/* Reason */}
                    {log.reason && (
                      <div className="ml-11 mb-2">
                        <p className="text-sm">
                          <strong className="text-gray-700">Motivo:</strong>
                          <span className="text-gray-600 ml-2">{log.reason}</span>
                        </p>
                      </div>
                    )}

                    {/* Notes */}
                    {log.notes && (
                      <div className="ml-11 mb-2">
                        <p className="text-sm">
                          <strong className="text-gray-700">Observações:</strong>
                          <span className="text-gray-600 ml-2">{log.notes}</span>
                        </p>
                      </div>
                    )}

                    {/* Metadata */}
                    {log.metadata && (
                      <div className="ml-11 mt-3 pt-3 border-t border-gray-200">
                        <details className="text-xs text-gray-500">
                          <summary className="cursor-pointer hover:text-gray-700">
                            Informações técnicas
                          </summary>
                          <div className="mt-2 space-y-1 pl-4">
                            {log.metadata.ip_address && (
                              <p>
                                <strong>IP:</strong> {log.metadata.ip_address}
                              </p>
                            )}
                            {log.metadata.user_agent && (
                              <p>
                                <strong>User Agent:</strong> {log.metadata.user_agent}
                              </p>
                            )}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLogsModal(false);
                  setPlanLogs([]);
                }}
                className="w-full"
              >
                Fechar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AssociatePlans;
